import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Toolbar,
  Typography,
  Button,
  Checkbox,
  Menu,
  MenuItem as MenuItemAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Mail as MailIcon,
  DraftsOutlined as DraftsOutlinedIcon,
  StarBorder as StarBorderIcon,
  Star as StarIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  MoreVert as MoreVertIcon,
  Send as SendIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  LinkOff as LinkOffIcon,
  Lightbulb as LightbulbIcon,
  AttachFile as AttachFileIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { emailsApi, customersApi } from '../../services/api';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { formatCustomerName } from '../../utils/formatCustomerName';
import AiCategoryBadge from '../../components/ai/AiCategoryBadge';

const AUTO_REFRESH_INTERVAL_MS = 30000;
const SYNC_STATUS_REFRESH_INTERVAL_MS = 15000;

export default function Inbox() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin';
  const hasConfiguredAccounts = accounts => Array.isArray(accounts) && accounts.length > 0;
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [folder, setFolder] = useState('INBOX');
  const [accountId, setAccountId] = useState('');
  const [unreadFilter, setUnreadFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentMessage, setCurrentMessage] = useState(null);
  
  // Stati per dialog associazione cliente
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [emailToAssign, setEmailToAssign] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  
  // Stati per dialog gestione email anagrafica
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);
  const [emailAction, setEmailAction] = useState('principale'); // 'principale', 'secondaria', 'sostituisci_principale', 'sostituisci_secondaria'
  const [savingEmail, setSavingEmail] = useState(false);
  const [enableInvoiceEmail, setEnableInvoiceEmail] = useState(true); // Checkbox invio fatture

  // Query accounts
  const { data: accounts = [] } = useQuery({
    queryKey: ['email-accounts'],
    queryFn: () => api.get('/email-accounts').then(res => res.data)
  });

  // Query messages
  const { data: messagesData, isLoading } = useQuery({
    queryKey: ['emails', { page, rowsPerPage, folder, accountId, unreadFilter, searchQuery }],
    queryFn: () => api.get('/emails', {
      params: {
        page: page + 1,
        limit: rowsPerPage,
        folder,
        account_id: accountId || undefined,
        unread: unreadFilter || undefined,
        search: searchQuery || undefined
      }
    }).then(res => res.data),
    enabled: hasConfiguredAccounts(accounts),
    refetchInterval: AUTO_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: 15000,
    refetchIntervalInBackground: false,
  });

  const { data: syncStatusData } = useQuery({
    queryKey: ['email-sync-status'],
    queryFn: () => api.get('/emails/sync-status').then(res => res.data),
    enabled: hasConfiguredAccounts(accounts),
    refetchInterval: SYNC_STATUS_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: 5000,
    refetchIntervalInBackground: false,
  });

  const messages = messagesData?.messages || [];
  const total = messagesData?.total || 0;
  const syncStatus = syncStatusData || {};
  
  // Corregge page se fuori range (es. dopo sync o cambio filtri)
  const maxPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
  const safePage = Math.min(page, maxPage);

  const formatSyncDateTime = (value) => {
    if (!value) return 'mai';
    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) return 'n/d';
    return format(dateValue, 'dd/MM HH:mm:ss', { locale: it });
  };

  const formatIntervalLabel = (intervalMs) => {
    if (!intervalMs) return 'n/d';
    const totalSeconds = Math.round(intervalMs / 1000);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.round(totalSeconds / 60);
    return `${minutes} min`;
  };

  const syncSummary = syncStatus.lastSummary;
  const syncHasErrors = Boolean(syncSummary?.errorCount);
  const syncSummaryText = syncSummary?.totalAccounts
    ? `${syncSummary.successCount}/${syncSummary.totalAccounts} account ok${syncSummary.errorCount ? `, ${syncSummary.errorCount} errori` : ''}${syncSummary.skippedCount ? `, ${syncSummary.skippedCount} skip` : ''}`
    : 'nessun ciclo completato ancora';

  // Sync manual mutation
  const syncMutation = useMutation({
    mutationFn: (accId) => api.post(`/email-accounts/${accId}/sync`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      queryClient.invalidateQueries({ queryKey: ['email-sync-status'] });
    }
  });

  // Sync ALL accounts mutation
  const syncAllMutation = useMutation({
    mutationFn: () => api.post('/emails/sync-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      queryClient.invalidateQueries({ queryKey: ['email-sync-status'] });
    }
  });

  let syncAlertSeverity = 'info';
  let syncAlertMessage = `Sync automatica attiva ogni ${formatIntervalLabel(syncStatus.intervalMs)}. In attesa del primo ciclo.`;

  if (syncAllMutation.isPending) {
    syncAlertMessage = 'Sincronizzazione manuale in corso. La lista si aggiorna automaticamente al termine.';
  } else if (syncStatus.enabled === false) {
    syncAlertSeverity = 'warning';
    syncAlertMessage = 'Sync automatica backend disabilitata. In questo stato il pulsante di sincronizzazione manuale resta l’unico fallback.';
  } else if (syncStatus.lastError) {
    syncAlertSeverity = 'warning';
    syncAlertMessage = `Ultimo errore sync: ${syncStatus.lastError}. Ultimo tentativo ${formatSyncDateTime(syncStatus.lastFinishedAt || syncStatus.lastStartedAt)}.`;
  } else if (syncStatus.running) {
    syncAlertMessage = `Sync automatica in corso. Ultimo avvio ${formatSyncDateTime(syncStatus.lastStartedAt)}.`;
  } else if (syncStatus.lastFinishedAt) {
    syncAlertSeverity = syncHasErrors ? 'warning' : 'success';
    syncAlertMessage = `Sync automatica attiva ogni ${formatIntervalLabel(syncStatus.intervalMs)}. Ultimo completamento ${formatSyncDateTime(syncStatus.lastFinishedAt)}: ${syncSummaryText}.`;
  }

  // Mark read mutation
  const markReadMutation = useMutation({
    mutationFn: ({ id, is_read }) => api.put(`/emails/${id}`, { is_read }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
    }
  });

  // Flag mutation
  const flagMutation = useMutation({
    mutationFn: ({ id, is_flagged }) => api.put(`/emails/${id}`, { is_flagged }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/emails/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
    }
  });

  // Assign customer mutation
  const assignCustomerMutation = useMutation({
    mutationFn: ({ emailId, customerId }) => emailsApi.assignCustomer(emailId, customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      setAssignDialogOpen(false);
      setEmailToAssign(null);
      setSuggestions([]);
    }
  });

  // Apri dialog associazione cliente
  const handleOpenAssignDialog = async (e, message) => {
    e.stopPropagation();
    setEmailToAssign(message);
    setAssignDialogOpen(true);
    setSuggestionsLoading(true);
    setSuggestions([]);
    setCustomerSearch('');
    
    try {
      const res = await emailsApi.suggestCustomers(message.id);
      setSuggestions(res.data?.suggestions || []);
    } catch (err) {
      console.error('Errore caricamento suggerimenti:', err);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  // Rimuovi associazione cliente
  const handleRemoveAssociation = (e, message) => {
    e.stopPropagation();
    if (window.confirm('Rimuovere l\'associazione con il cliente?')) {
      assignCustomerMutation.mutate({ emailId: message.id, customerId: null });
    }
  };

  // Associa cliente selezionato - ora apre dialog per gestione email
  const handleAssignCustomer = async (customerId) => {
    if (!emailToAssign || !customerId) return;
    
    try {
      // Carica i dati completi del cliente
      const res = await customersApi.getById(customerId);
      const customer = res.data?.data || res.data; // Supporta entrambi i formati
      setSelectedCustomerData(customer);
      
      // Determina l'azione di default in base alle email esistenti
      if (!customer.email && !customer.email_secondaria) {
        setEmailAction('principale');
        setEnableInvoiceEmail(true);
      } else if (customer.email && !customer.email_secondaria) {
        setEmailAction('secondaria');
        setEnableInvoiceEmail(false);
      } else {
        // Ha entrambe le email - default a sostituire la secondaria
        setEmailAction('sostituisci_secondaria');
        setEnableInvoiceEmail(false);
      }
      
      // Apri il dialog per gestione email
      setEmailDialogOpen(true);
    } catch (err) {
      console.error('Errore caricamento cliente:', err);
      // In caso di errore, associa direttamente senza gestione email
      assignCustomerMutation.mutate({ emailId: emailToAssign.id, customerId });
    }
  };

  // Estrai indirizzo email dal mittente
  const getSenderEmail = () => {
    if (!emailToAssign) return '';
    return emailToAssign.from_address || '';
  };

  // Salva email in anagrafica cliente e associa
  const handleSaveEmailAndAssign = async () => {
    if (!emailToAssign || !selectedCustomerData) return;
    
    setSavingEmail(true);
    const senderEmail = getSenderEmail();
    
    try {
      // Prepara i dati da aggiornare
      const updateData = {};
      
      if (emailAction === 'principale' || emailAction === 'sostituisci_principale') {
        updateData.email = senderEmail;
        // Usa il valore del checkbox per l'invio fatture
        if (enableInvoiceEmail) {
          updateData.invio_email = true;
        }
      } else if (emailAction === 'secondaria' || emailAction === 'sostituisci_secondaria') {
        updateData.email_secondaria = senderEmail;
      }
      
      // Aggiorna il cliente
      await customersApi.update(selectedCustomerData.id, updateData);
      
      // Associa l'email al cliente
      await assignCustomerMutation.mutateAsync({ 
        emailId: emailToAssign.id, 
        customerId: selectedCustomerData.id 
      });
      
      // Chiudi i dialog
      setEmailDialogOpen(false);
      setAssignDialogOpen(false);
      setSelectedCustomerData(null);
      setEmailToAssign(null);
      
    } catch (err) {
      console.error('Errore salvataggio email:', err);
      alert('Errore durante il salvataggio: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingEmail(false);
    }
  };

  // Associa senza salvare email in anagrafica
  const handleAssignWithoutEmail = () => {
    if (emailToAssign && selectedCustomerData) {
      assignCustomerMutation.mutate({ 
        emailId: emailToAssign.id, 
        customerId: selectedCustomerData.id 
      });
      setEmailDialogOpen(false);
      setSelectedCustomerData(null);
    }
  };

  // Cerca clienti per autocomplete
  const handleSearchCustomers = async (searchText) => {
    if (searchText.length < 2) {
      setCustomerOptions([]);
      return;
    }
    try {
      const res = await customersApi.getAll({ search: searchText, limit: 20 });
      setCustomerOptions(res.data?.data || []);
    } catch (err) {
      console.error('Errore ricerca clienti:', err);
    }
  };

  const handleSyncAll = () => {
    syncAllMutation.mutate();
  };

  const handleRowClick = (message) => {
    navigate(`/crm/email/${message.id}`);
  };

  const handleToggleRead = (e, message) => {
    e.stopPropagation();
    markReadMutation.mutate({ id: message.id, is_read: !message.is_read });
  };

  const handleToggleFlagged = (e, message) => {
    e.stopPropagation();
    flagMutation.mutate({ id: message.id, is_flagged: !message.is_flagged });
  };

  const handleDelete = (e, message) => {
    e.stopPropagation();
    if (window.confirm('Eliminare questa email?')) {
      deleteMutation.mutate(message.id);
    }
  };

  const handleMenuOpen = (e, message) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setCurrentMessage(message);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentMessage(null);
  };

  const handleAddToBlacklist = async () => {
    if (!currentMessage?.from_address) {
      alert('Nessun indirizzo email da aggiungere');
      return;
    }
    
    if (window.confirm(`Aggiungere "${currentMessage.from_address}" alla blacklist?\nLe email da questo indirizzo non verranno più sincronizzate.`)) {
      try {
        await api.post('/company-settings/add-to-blacklist', { email: currentMessage.from_address });
        alert(`Email "${currentMessage.from_address}" aggiunta alla blacklist`);
        handleMenuClose();
      } catch (err) {
        alert('Errore: ' + (err.response?.data?.message || 'Impossibile aggiungere alla blacklist'));
      }
    }
  };

  const handleReply = () => {
    navigate(`/crm/email/compose?replyTo=${currentMessage.id}`);
    handleMenuClose();
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Paper>
        <Toolbar sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, fontSize: isMobile ? '1rem' : undefined }}>
            Inbox Email
          </Typography>
          {!isMobile && <Button
            variant="outlined"
            sx={{ mr: 1 }}
            onClick={() => navigate('/crm/email/accounts')}
          >
            Account
          </Button>}
          {!isMobile && <Button
            variant="outlined"
            sx={{ mr: 1 }}
            onClick={() => navigate('/crm/email/ai-dashboard')}
            startIcon={<LightbulbIcon />}
          >
            AI Assistant
          </Button>}
          <Button
            startIcon={<SendIcon />}
            variant="contained"
            onClick={() => navigate('/crm/email/compose')}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Nuova' : 'Nuova Email'}
          </Button>
        </Toolbar>

        {hasConfiguredAccounts(accounts) && (
          <Box sx={{ px: isMobile ? 1 : 2, pt: 1 }}>
            <Alert severity={syncAlertSeverity} sx={{ mb: 1 }}>
              {syncAlertMessage}
            </Alert>
          </Box>
        )}

        {accounts.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Paper sx={{ p: 2 }} variant="outlined">
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Nessun account email configurato
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
                {isAdmin
                  ? 'Per leggere e inviare email devi prima aggiungere un account (IMAP/SMTP).'
                  : "Per leggere e inviare email serve un account configurato da un amministratore."}
              </Typography>
              {isAdmin && (
                <Button variant="contained" onClick={() => navigate('/crm/email/accounts')}>
                  Configura account email
                </Button>
              )}
            </Paper>
          </Box>
        )}

        <Box sx={{ p: isMobile ? 1 : 2, display: 'flex', gap: isMobile ? 1 : 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: { xs: '45%', sm: 150 } }}>
            <InputLabel>Cartella</InputLabel>
            <Select value={folder} onChange={(e) => setFolder(e.target.value)} label="Cartella">
              <MenuItem value="INBOX">Ricevuta</MenuItem>
              <MenuItem value="Sent">Inviata</MenuItem>
              <MenuItem value="Archive">Archivio</MenuItem>
              <MenuItem value="Drafts">Bozze</MenuItem>
              <MenuItem value="Trash">Cestino</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: '45%', sm: 200 } }}>
            <InputLabel>Account</InputLabel>
            <Select value={accountId} onChange={(e) => setAccountId(e.target.value)} label="Account">
              <MenuItem value="">Tutti</MenuItem>
              {accounts.map(acc => (
                <MenuItem key={acc.id} value={acc.id}>{acc.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small">
            <Button
              variant={unreadFilter ? 'contained' : 'outlined'}
              onClick={() => setUnreadFilter(!unreadFilter)}
              startIcon={<MailIcon />}
            >
              Solo non lette
            </Button>
          </FormControl>

          <TextField
            size="small"
            placeholder="Cerca..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
            InputProps={{
              endAdornment: <SearchIcon />
            }}
          />

          <IconButton onClick={handleSyncAll} disabled={syncAllMutation.isPending}>
            {syncAllMutation.isPending ? <CircularProgress size={24} /> : <RefreshIcon />}
          </IconButton>
        </Box>

        {isLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>
        ) : messages.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: 'center' }} color="text.secondary">Nessuna email</Typography>
        ) : isMobile ? (
          <Box sx={{ px: 1 }}>
              {messages.map((message) => (
              <Paper
                key={message.id}
                variant="outlined"
                onClick={() => handleRowClick(message)}
                sx={{
                  p: 1.5, mb: 1, cursor: 'pointer',
                  bgcolor: message.is_read ? 'transparent' : 'action.hover',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={message.is_read ? 'normal' : 'bold'} noWrap sx={{ flex: 1, mr: 1 }}>
                    {(message.folder === 'Sent' || message.folder?.toLowerCase().includes('sent'))
                      ? (() => { try { const to = message.to_addresses ? JSON.parse(message.to_addresses) : []; const first = to[0]; return `A: ${typeof first === 'string' ? first : (first?.name || first?.address)}`; } catch (e) { return message.from_address; } })()
                      : (message.from_name || message.from_address)
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {format(new Date(message.date_sent), 'dd/MM HH:mm', { locale: it })}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={message.is_read ? 'normal' : 'bold'} noWrap>
                  {message.subject}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                    {message.has_attachments && <Chip icon={<AttachFileIcon />} label={message.attachments_count || ''} size="small" variant="outlined" />}
                    {message.customer && <Chip label={formatCustomerName(message.customer)} size="small" color="primary" variant="outlined" />}
                    {message.ai_category && <AiCategoryBadge category={message.ai_category} confidence={message.ai_confidence} size="small" showConfidence={false} />}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleFlagged(e, message); }}>
                      {message.is_flagged ? <StarIcon color="warning" fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, message); }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < messages.length}
                    checked={messages.length > 0 && selectedIds.length === messages.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(messages.map(m => m.id));
                      } else {
                        setSelectedIds([]);
                      }
                    }}
                  />
                </TableCell>
                <TableCell width={50}></TableCell>
                <TableCell>Da</TableCell>
                <TableCell>Oggetto</TableCell>
                <TableCell width={100}>Allegati</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Data</TableCell>
                <TableCell width={100}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((message) => (
                  <TableRow
                    key={message.id}
                    hover
                    onClick={() => handleRowClick(message)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: message.is_read ? 'transparent' : 'action.hover',
                      fontWeight: message.is_read ? 'normal' : 'bold'
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(message.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (e.target.checked) {
                            setSelectedIds([...selectedIds, message.id]);
                          } else {
                            setSelectedIds(selectedIds.filter(id => id !== message.id));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(e) => handleToggleFlagged(e, message)}>
                        {message.is_flagged ? <StarIcon color="warning" /> : <StarBorderIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontWeight: message.is_read ? 'normal' : 'bold' }}>
                      {(message.folder === 'Sent' || message.folder === 'INBOX.Sent' || message.folder?.toLowerCase().includes('sent')) ? (
                        <>
                          <span style={{ color: '#666' }}>A: </span>
                          {(() => {
                            try {
                              const to = message.to_addresses ? JSON.parse(message.to_addresses) : [];
                              if (to.length === 0) return '-';
                              const first = to[0];
                              const addr = typeof first === 'string' ? first : (first.name || first.address);
                              return to.length > 1 ? `${addr} +${to.length - 1}` : addr;
                            } catch (e) { return message.from_address; }
                          })()}
                        </>
                      ) : (
                        message.from_name || message.from_address
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: message.is_read ? 'normal' : 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{message.subject}</span>
                        {message.ai_category && <AiCategoryBadge category={message.ai_category} confidence={message.ai_confidence} size="small" showConfidence={false} />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {message.has_attachments && (
                        <Chip 
                          icon={<AttachFileIcon />}
                          label={message.attachments_count || '?'}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {message.customer ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip 
                            label={formatCustomerName(message.customer)} 
                            size="small" 
                            color="primary"
                            variant="outlined"
                            clickable
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/crm/clienti/${message.customer.id}`);
                            }}
                          />
                          <Tooltip title="Rimuovi associazione">
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleRemoveAssociation(e, message)}
                              sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
                            >
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Tooltip title="Associa cliente">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={(e) => handleOpenAssignDialog(e, message)}
                          >
                            <PersonAddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {format(new Date(message.date_sent), 'dd MMM yyyy HH:mm', { locale: it })}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(e) => handleToggleRead(e, message)}>
                        {message.is_read ? <DraftsOutlinedIcon /> : <MailIcon color="primary" />}
                      </IconButton>
                      <IconButton size="small" onClick={(e) => handleDelete(e, message)}>
                        <DeleteIcon />
                      </IconButton>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, message)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        <TablePagination
          component="div"
          count={total}
          page={safePage}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value));
            setPage(0);
          }}
          labelRowsPerPage="Email per pagina"
        />
      </Paper>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItemAction onClick={handleReply}>
          <ReplyIcon sx={{ mr: 1 }} /> Rispondi
        </MenuItemAction>
        <MenuItemAction onClick={() => {
          navigate(`/crm/email/${currentMessage.id}`);
          handleMenuClose();
        }}>
          <MailIcon sx={{ mr: 1 }} /> Visualizza
        </MenuItemAction>
        <Divider />
        <MenuItemAction onClick={handleAddToBlacklist}>
          <BlockIcon sx={{ mr: 1 }} /> Aggiungi a Blacklist
        </MenuItemAction>
      </Menu>

      {/* Dialog Associazione Cliente */}
      <Dialog 
        open={assignDialogOpen} 
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Associa Cliente all'Email
        </DialogTitle>
        <DialogContent>
          {emailToAssign && (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Oggetto:
              </Typography>
              <Typography variant="body2" noWrap>
                {emailToAssign.subject || '(Nessun oggetto)'}
              </Typography>
            </Box>
          )}

          {/* Ricerca manuale cliente - IN CIMA */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Cerca cliente
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Digita nome, codice cliente, ragione sociale..."
            value={customerSearch}
            onChange={(e) => {
              setCustomerSearch(e.target.value);
              handleSearchCustomers(e.target.value);
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ mb: 2 }}
          />
          
          {/* Risultati ricerca */}
          {customerSearch.length >= 2 && (
            <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
              {customerOptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  Nessun cliente trovato per "{customerSearch}"
                </Typography>
              ) : (
                <List dense>
                  {customerOptions.map((customer) => (
                    <ListItem key={customer.id} disablePadding>
                      <ListItemButton onClick={() => handleAssignCustomer(customer.id)}>
                        <ListItemIcon>
                          <PersonIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label={customer.cod_cli} size="small" />
                              <Typography>
                                {customer.nome} {customer.cognome}
                              </Typography>
                            </Box>
                          }
                          secondary={customer.ragione_sociale || customer.email}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
          
          {customerSearch.length > 0 && customerSearch.length < 2 && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Digita almeno 2 caratteri per cercare...
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Suggerimenti automatici */}
          {suggestionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Analisi contenuto email...</Typography>
            </Box>
          ) : suggestions.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LightbulbIcon sx={{ mr: 1, color: 'warning.main' }} />
                Clienti suggeriti (match automatico)
              </Typography>
              <Alert severity="info" sx={{ mb: 1 }} icon={false}>
                Trovati {suggestions.length} possibili match nel contenuto dell'email
              </Alert>
              <List dense>
                {suggestions.map((suggestion) => (
                  <ListItem key={suggestion.customer.id} disablePadding>
                    <ListItemButton onClick={() => handleAssignCustomer(suggestion.customer.id)}>
                      <ListItemIcon>
                        <PersonIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography fontWeight="medium">
                              {suggestion.customer.nome} {suggestion.customer.cognome}
                            </Typography>
                            <Chip label={suggestion.customer.cod_cli} size="small" />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {suggestion.reasons.join(' • ')}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Alert severity="info" icon={<LightbulbIcon />}>
              Nessun match automatico trovato nel contenuto dell'email.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>
            Annulla
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Gestione Email Anagrafica */}
      <Dialog 
        open={emailDialogOpen} 
        onClose={() => !savingEmail && setEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Aggiungi Email in Anagrafica Cliente
        </DialogTitle>
        <DialogContent>
          {selectedCustomerData && (
            <>
              {/* Info cliente */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="subtitle2" color="primary.main" gutterBottom>
                  Cliente selezionato:
                </Typography>
                <Typography variant="h6">
                  {selectedCustomerData.nome} {selectedCustomerData.cognome}
                </Typography>
                {selectedCustomerData.ragione_sociale && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedCustomerData.ragione_sociale}
                  </Typography>
                )}
                <Chip label={selectedCustomerData.cod_cli} size="small" sx={{ mt: 1 }} />
              </Box>

              {/* Email da aggiungere */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Indirizzo email da aggiungere:
                </Typography>
                <Typography variant="h6" color="primary">
                  {getSenderEmail()}
                </Typography>
              </Box>

              {/* Situazione attuale email */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Email attualmente in anagrafica:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                  <Typography variant="body2">
                    <strong>Principale:</strong> {selectedCustomerData.email || <em style={{ color: '#999' }}>Non impostata</em>}
                    {selectedCustomerData.invio_email && selectedCustomerData.email && (
                      <Chip label="Invio fatture attivo" size="small" color="success" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Secondaria:</strong> {selectedCustomerData.email_secondaria || <em style={{ color: '#999' }}>Non impostata</em>}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Scelta azione */}
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormLabel component="legend" sx={{ mb: 2 }}>
                  Come vuoi salvare questo indirizzo email?
                </FormLabel>
                <RadioGroup
                  value={emailAction}
                  onChange={(e) => setEmailAction(e.target.value)}
                >
                  {/* Caso 1: Nessuna email presente */}
                  {!selectedCustomerData.email && !selectedCustomerData.email_secondaria && (
                    <FormControlLabel 
                      value="principale" 
                      control={<Radio />} 
                      label={
                        <Box>
                          <Typography>Imposta come email principale</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Sarà usata per l'invio delle fatture e comunicazioni
                          </Typography>
                        </Box>
                      }
                    />
                  )}

                  {/* Caso 2: Solo email principale presente */}
                  {selectedCustomerData.email && !selectedCustomerData.email_secondaria && (
                    <>
                      <FormControlLabel 
                        value="secondaria" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography>Aggiungi come email secondaria</Typography>
                            <Typography variant="caption" color="text.secondary">
                              L'email principale ({selectedCustomerData.email}) rimane invariata
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel 
                        value="sostituisci_principale" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography color="warning.main">Sostituisci email principale</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sovrascrive "{selectedCustomerData.email}"
                            </Typography>
                          </Box>
                        }
                      />
                    </>
                  )}

                  {/* Caso 3: Entrambe le email presenti */}
                  {selectedCustomerData.email && selectedCustomerData.email_secondaria && (
                    <>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Il cliente ha già entrambi gli indirizzi email configurati. 
                        Seleziona quale vuoi sovrascrivere.
                      </Alert>
                      <FormControlLabel 
                        value="sostituisci_principale" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography color="warning.main">Sostituisci email principale</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sovrascrive "{selectedCustomerData.email}"
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel 
                        value="sostituisci_secondaria" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography color="warning.main">Sostituisci email secondaria</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sovrascrive "{selectedCustomerData.email_secondaria}"
                            </Typography>
                          </Box>
                        }
                      />
                    </>
                  )}

                  {/* Caso 4: Solo email secondaria presente (raro) */}
                  {!selectedCustomerData.email && selectedCustomerData.email_secondaria && (
                    <>
                      <FormControlLabel 
                        value="principale" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography>Imposta come email principale</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sarà usata per l'invio delle fatture e comunicazioni
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel 
                        value="sostituisci_secondaria" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography color="warning.main">Sostituisci email secondaria</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sovrascrive "{selectedCustomerData.email_secondaria}"
                            </Typography>
                          </Box>
                        }
                      />
                    </>
                  )}
                </RadioGroup>
              </FormControl>

              {/* Checkbox invio fatture - solo se email principale */}
              {(emailAction === 'principale' || emailAction === 'sostituisci_principale') && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={enableInvoiceEmail} 
                        onChange={(e) => setEnableInvoiceEmail(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography>Attiva invio fatture via email</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Le fatture verranno inviate automaticamente a questo indirizzo
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setEmailDialogOpen(false);
              setSelectedCustomerData(null);
            }}
            disabled={savingEmail}
          >
            Annulla
          </Button>
          <Button 
            variant="outlined"
            onClick={handleAssignWithoutEmail}
            disabled={savingEmail}
          >
            Associa senza salvare email
          </Button>
          <Button 
            variant="contained"
            onClick={handleSaveEmailAndAssign}
            disabled={savingEmail}
          >
            {savingEmail ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Salva e Associa
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
